import IntelligentContentParser from './IntelligentContentParser';
import {
  isAmmunitionEntry,
  isExplicitlyEquippedWeapon,
  normalizeEquipmentInventoryEntry
} from './EquipmentState';

const createEmptyEquipment = () => ({
  weapons: [],
  armor: [],
  items: [],
  valuables: [],
  currency: {}
});

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined) return [];
  return [value];
};

const chooseLongerString = (left, right) => {
  if (typeof left !== 'string' || !left.trim()) return right;
  if (typeof right !== 'string' || !right.trim()) return left;
  return right.trim().length >= left.trim().length ? right : left;
};

const getSourceField = (entry, field) => (
  field ||
  entry?.importMeta?.sourceField ||
  entry?.sourceSection ||
  entry?.parserMeta?.sourceSection ||
  entry?.type ||
  'entry'
);

const getNormalizedName = (entry) => IntelligentContentParser.normalizeParsedEntryName(
  typeof entry === 'string' ? entry : (entry?.name || entry?.header || entry?.title || '')
);

const getNormalizedDescription = (entry) => IntelligentContentParser.normalizeParsedEntryName(
  typeof entry === 'string' ? '' : (entry?.description || entry?.content || entry?.statLine || entry?.rawLine || '')
);

const getFingerprint = (entry, field) => {
  if (!entry) return '';
  if (typeof entry === 'object' && entry?.importMeta?.smartPasteFingerprint) {
    return entry.importMeta.smartPasteFingerprint;
  }
  return IntelligentContentParser.buildParsedEntryFingerprint(entry, getSourceField(entry, field));
};

const getConflictResolution = (entry) => (
  entry?.importMeta?.conflictResolution ||
  'merge'
);

export const buildSmartPasteImportMeta = (entry, field) => {
  if (!entry || typeof entry !== 'object') return null;

  const sourceField = getSourceField(entry, field);
  const parserMeta = IntelligentContentParser.mergeParserMeta(
    entry.importMeta?.parserMeta,
    entry.parserMeta
  );
  const importMeta = {
    ...(entry.importMeta || {}),
    source: 'smartPaste',
    sourceField,
    smartPasteFingerprint: entry.importMeta?.smartPasteFingerprint ||
      IntelligentContentParser.buildParsedEntryFingerprint(entry, sourceField)
  };

  if (parserMeta && Object.keys(parserMeta).length > 0) {
    importMeta.parserMeta = parserMeta;
  }

  return importMeta;
};

export const stampSmartPasteEntry = (entry, field) => {
  if (!entry || typeof entry !== 'object') return entry;

  // date_of_change: 2026-07-29
  // rule_reference: SmartPaste append, replace, and full-sheet paths must converge on the
  // first-class character inventory contract before entries are fingerprinted or bucketed.
  const normalizedEntry = field === 'equipment'
    ? normalizeEquipmentInventoryEntry(entry)
    : entry;
  const importMeta = buildSmartPasteImportMeta(normalizedEntry, field);
  const parserMeta = importMeta?.parserMeta || undefined;

  const stampedEntry = {
    ...normalizedEntry,
    ...(parserMeta ? { parserMeta } : {}),
    ...(importMeta ? { importMeta } : {})
  };

  if (field === 'equipment') {
    return IntelligentContentParser.normalizeEquipmentWeaponEntry(stampedEntry);
  }

  return stampedEntry;
};

export const stampSmartPasteEntryList = (entries, field) => (
  toArray(entries)
    .filter(Boolean)
    .map((entry) => stampSmartPasteEntry(entry, field))
);

export const smartPasteEntriesMatch = (existingEntry, incomingEntry, field) => {
  if (!existingEntry || !incomingEntry) return false;

  const existingFingerprint = getFingerprint(existingEntry, field);
  const incomingFingerprint = getFingerprint(incomingEntry, field);
  if (existingFingerprint && incomingFingerprint && existingFingerprint === incomingFingerprint) {
    return true;
  }

  const existingName = getNormalizedName(existingEntry);
  const incomingName = getNormalizedName(incomingEntry);
  if (!existingName || existingName !== incomingName) {
    return false;
  }

  const existingDescription = getNormalizedDescription(existingEntry);
  const incomingDescription = getNormalizedDescription(incomingEntry);
  if (!existingDescription || !incomingDescription) {
    return true;
  }

  return (
    existingDescription === incomingDescription ||
    existingDescription.includes(incomingDescription) ||
    incomingDescription.includes(existingDescription)
  );
};

export const findSmartPasteConflictCandidates = (existingEntries, incomingEntry, field) => {
  const incomingName = getNormalizedName(incomingEntry);
  if (!incomingName) return [];

  return toArray(existingEntries)
    .map((existingEntry, index) => {
      if (!existingEntry) return null;

      const existingName = getNormalizedName(existingEntry);
      if (!existingName || existingName !== incomingName) return null;

      const exactMatch = smartPasteEntriesMatch(existingEntry, incomingEntry, field);
      return {
        index,
        existingEntry,
        matchType: exactMatch ? 'exact' : 'sameName',
        fingerprint: getFingerprint(existingEntry, field),
        name: existingEntry?.name || '',
        description: existingEntry?.description || existingEntry?.content || existingEntry?.statLine || ''
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      if (left.matchType === right.matchType) return 0;
      return left.matchType === 'exact' ? -1 : 1;
    });
};

const findExistingEntryIndex = (existingEntries, incomingEntry, field, allowNameFallback = false) => {
  const candidates = findSmartPasteConflictCandidates(existingEntries, incomingEntry, field);
  if (candidates.length === 0) return -1;

  const preferredFingerprint = incomingEntry?.importMeta?.conflictTargetFingerprint;
  if (preferredFingerprint) {
    const preferredCandidate = candidates.find((candidate) => candidate.fingerprint === preferredFingerprint);
    if (preferredCandidate) return preferredCandidate.index;
  }

  const exactCandidate = candidates.find((candidate) => candidate.matchType === 'exact');
  if (exactCandidate) return exactCandidate.index;

  return allowNameFallback ? candidates[0].index : -1;
};

export const mergeSmartPasteEntry = (existingEntry, incomingEntry, field) => {
  if (!incomingEntry) return existingEntry;
  if (!existingEntry) return stampSmartPasteEntry(incomingEntry, field);
  if (typeof existingEntry !== 'object' || typeof incomingEntry !== 'object') {
    return stampSmartPasteEntry(incomingEntry, field);
  }

  const mergedParserMeta = IntelligentContentParser.mergeParserMeta(
    existingEntry.parserMeta,
    incomingEntry.parserMeta,
    existingEntry.importMeta?.parserMeta,
    incomingEntry.importMeta?.parserMeta
  );
  const merged = {
    ...existingEntry,
    ...incomingEntry,
    name: incomingEntry.name || existingEntry.name,
    description: chooseLongerString(existingEntry.description, incomingEntry.description),
    content: chooseLongerString(existingEntry.content, incomingEntry.content),
    statLine: chooseLongerString(existingEntry.statLine, incomingEntry.statLine),
    quantity: incomingEntry.quantity ?? existingEntry.quantity
  };

  if (mergedParserMeta && Object.keys(mergedParserMeta).length > 0) {
    merged.parserMeta = mergedParserMeta;
  }

  const mergedImportMeta = buildSmartPasteImportMeta({
    ...merged,
    importMeta: {
      ...(existingEntry.importMeta || {}),
      ...(incomingEntry.importMeta || {})
    },
    parserMeta: mergedParserMeta
  }, field);

  if (mergedImportMeta) {
    merged.importMeta = mergedImportMeta;
  }

  return merged;
};

export const replaceSmartPasteEntry = (existingEntry, incomingEntry, field) => {
  if (!incomingEntry) return existingEntry;
  if (!existingEntry) return stampSmartPasteEntry(incomingEntry, field);
  if (typeof existingEntry !== 'object' || typeof incomingEntry !== 'object') {
    return stampSmartPasteEntry(incomingEntry, field);
  }

  const mergedParserMeta = IntelligentContentParser.mergeParserMeta(
    existingEntry.parserMeta,
    incomingEntry.parserMeta,
    existingEntry.importMeta?.parserMeta,
    incomingEntry.importMeta?.parserMeta
  );
  const replaced = {
    ...existingEntry,
    ...incomingEntry,
    parserMeta: mergedParserMeta
  };
  const importMeta = buildSmartPasteImportMeta({
    ...replaced,
    importMeta: {
      ...(existingEntry.importMeta || {}),
      ...(incomingEntry.importMeta || {})
    },
    parserMeta: mergedParserMeta
  }, field);

  if (importMeta) {
    replaced.importMeta = importMeta;
  }

  return replaced;
};

export const mergeSmartPasteEntryList = (existingEntries, incomingEntries, field) => {
  const merged = stampSmartPasteEntryList(existingEntries, field);

  toArray(incomingEntries)
    .filter(Boolean)
    .forEach((incomingEntry) => {
      const stampedIncoming = stampSmartPasteEntry(incomingEntry, field);
      const conflictResolution = getConflictResolution(stampedIncoming);

      if (conflictResolution === 'keepBoth') {
        merged.push(stampedIncoming);
        return;
      }

      const existingIndex = findExistingEntryIndex(
        merged,
        stampedIncoming,
        field,
        conflictResolution === 'replace'
      );

      if (existingIndex === -1) {
        merged.push(stampedIncoming);
        return;
      }

      merged[existingIndex] = conflictResolution === 'replace'
        ? replaceSmartPasteEntry(merged[existingIndex], stampedIncoming, field)
        : mergeSmartPasteEntry(merged[existingIndex], stampedIncoming, field);
    });

  return merged;
};

const bucketEquipmentEntry = (buckets, entry) => {
  const stampedEntry = stampSmartPasteEntry(entry, 'equipment');
  if (!stampedEntry) return;

  if (
    stampedEntry.type === 'weapon'
    && !isAmmunitionEntry(stampedEntry)
    && isExplicitlyEquippedWeapon(stampedEntry, 'equipment')
  ) {
    buckets.weapons.push(stampedEntry);
  } else if (stampedEntry.type === 'armor' || stampedEntry.type === 'shield') {
    buckets.armor.push(stampedEntry);
  } else if (stampedEntry.type === 'valuable') {
    buckets.valuables.push(stampedEntry);
  } else {
    buckets.items.push(stampedEntry);
  }
};

export const stampSmartPasteEquipment = (equipment) => {
  const buckets = createEmptyEquipment();
  if (!equipment) return buckets;

  if (Array.isArray(equipment)) {
    equipment.forEach((entry) => bucketEquipmentEntry(buckets, entry));
    return buckets;
  }

  ['weapons', 'armor', 'items', 'valuables'].forEach((bucket) => {
    buckets[bucket] = stampSmartPasteEntryList(equipment?.[bucket], 'equipment');
  });
  buckets.currency = {
    ...(equipment?.currency && typeof equipment.currency === 'object' ? equipment.currency : {})
  };

  return buckets;
};

export const mergeSmartPasteEquipment = (existingEquipment, incomingEquipment) => {
  const existingBuckets = stampSmartPasteEquipment(existingEquipment);
  const incomingBuckets = stampSmartPasteEquipment(incomingEquipment);

  return {
    weapons: mergeSmartPasteEntryList(existingBuckets.weapons, incomingBuckets.weapons, 'equipment'),
    armor: mergeSmartPasteEntryList(existingBuckets.armor, incomingBuckets.armor, 'equipment'),
    items: mergeSmartPasteEntryList(existingBuckets.items, incomingBuckets.items, 'equipment'),
    valuables: mergeSmartPasteEntryList(existingBuckets.valuables, incomingBuckets.valuables, 'equipment'),
    currency: {
      ...(existingBuckets.currency || {}),
      ...(incomingBuckets.currency || {})
    }
  };
};

const SmartPasteEntryMerge = {
  buildSmartPasteImportMeta,
  stampSmartPasteEntry,
  stampSmartPasteEntryList,
  smartPasteEntriesMatch,
  findSmartPasteConflictCandidates,
  mergeSmartPasteEntry,
  replaceSmartPasteEntry,
  mergeSmartPasteEntryList,
  stampSmartPasteEquipment,
  mergeSmartPasteEquipment
};

export default SmartPasteEntryMerge;
